import { useState } from "react";
import { MOCK_CONVERSATIONS, getUserById } from "../lib/mockData";
import MessageRequestCard from "../components/MessageRequestCard";
import { useT } from "../lib/useLanguage";
import LogoSwap from "../components/LogoSwap";
import LanguageToggle from "../components/LanguageToggle";
import { Shield, MessageCircle, Clock, Archive, Send } from "lucide-react";
import { motion } from "framer-motion";

// Tab order: Actifs, Reçus, Envoyés, Archivés
const TABS = [
  { key: "active", icon: MessageCircle, fr: "Actifs", en: "Active", filter: c => c.tab === "active" },
  { key: "received", icon: Clock, fr: "Reçus", en: "Received", filter: c => c.tab === "received" },
  { key: "sent", icon: Send, fr: "Envoyés", en: "Sent", filter: c => c.tab === "sent" },
  { key: "archived", icon: Archive, fr: "Archivés", en: "Archived", filter: c => c.tab === "archived" },
];

const EMPTY = {
  active: { fr: "Pas encore de conversations actives.", en: "No active conversations yet." },
  received: { fr: "Pas encore de demandes reçues.", en: "No requests received yet." },
  sent: { fr: "Tu n'as pas encore envoyé de demande.", en: "You haven't sent any request yet." },
  archived: { fr: "Aucune conversation archivée.", en: "No archived conversations." },
};

export default function Messages() {
  const t = useT();
  const lang = localStorage.getItem("swap_lang") || "fr";
  const [activeTab, setActiveTab] = useState("active");
  const tab = TABS.find(t => t.key === activeTab);
  const filtered = MOCK_CONVERSATIONS.filter(tab.filter);

  return (
    <div className="max-w-lg mx-auto px-4 pt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <LogoSwap size="sm" />
        <LanguageToggle />
      </div>

      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-xl font-extrabold text-foreground">{t("nav_messages")}</h1>
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
            <Shield className="h-3 w-3" style={{ color: "#FF6B4A" }} />
            {lang === "en" ? "One request until they reply" : "Une seule demande tant que la personne n'a pas répondu"}
          </p>
        </div>
        <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: "#FF6B4A15", color: "#FF6B4A" }}>
          {MOCK_CONVERSATIONS.filter(c => c.tab === "received" && c.status === "pending").length} nouveau
        </span>
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-4 gap-1 p-1 rounded-2xl mb-5" style={{ backgroundColor: "#f0ebe3" }}>
        {TABS.map(tab => {
          const active = activeTab === tab.key;
          const count = MOCK_CONVERSATIONS.filter(tab.filter).length;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex flex-col items-center py-2 rounded-xl text-xs font-bold transition-all gap-0.5"
              style={{ backgroundColor: active ? "white" : "transparent", color: active ? "#FF6B4A" : "#9ca3af", boxShadow: active ? "0 1px 4px rgba(0,0,0,0.08)" : "none" }}>
              <tab.icon className="h-4 w-4" />
              <span>{lang === "en" ? tab.en : tab.fr}</span>
              {count > 0 && <span className="text-[9px] font-black">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mb-3"
            style={{ backgroundColor: "#FF6B4A15" }}>✉</div>
          <p className="text-sm text-muted-foreground">{EMPTY[activeTab][lang]}</p>
        </motion.div>
      ) : (
        <div>
          {filtered.map((conv, i) => (
            <motion.div key={conv.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <MessageRequestCard
                conv={conv}
                isReceived={activeTab === "received"}
              />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}